import React from 'react';
import { GCSFieldsType } from './StorageTypeFields';
import { Typography } from '@mui/material';
import BucketIcon from '@mui/icons-material/Inventory2';
import EmailIcon from '@mui/icons-material/Email';
import KeyIcon from '@mui/icons-material/VpnKey';
import LinkIcon from '@mui/icons-material/Link';

interface GCSFieldsProps extends GCSFieldsType {
  validationErrors: {
    clientEmail?: string;
    privateKey?: string;
    bucket?: string;
  };
  onChange: (field: string, value: string) => void;
  isEdit?: boolean;
}

const GCSFields: React.FC<GCSFieldsProps> = ({
  bucket,
  clientEmail,
  privateKey,
  endpoint,
  validationErrors,
  onChange,
  isEdit = false,
}) => {
  return (
    <div className="space-y-6">
      <div className="border-b border-gray-100 pb-6">
        <Typography variant="h6" className="text-gray-800 font-medium mb-4">
          Google Cloud Storage Configuration
        </Typography>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bucket <span className="text-red-500">*</span>
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <BucketIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={bucket}
                onChange={(e) => onChange('bucket', e.target.value)}
                placeholder="my-bucket-gcs"
                className={`pl-10 block w-full rounded-md border ${
                  validationErrors.bucket ? 'border-red-500' : 'border-gray-300'
                } bg-white py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm`}
              />
            </div>
            {validationErrors.bucket && (
              <p className="mt-1 text-sm text-red-500">{validationErrors.bucket}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client Email <span className="text-red-500">*</span>
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <EmailIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => onChange('clientEmail', e.target.value)}
                placeholder="service-account@project.iam.gserviceaccount.com"
                className={`pl-10 block w-full rounded-md border ${
                  validationErrors.clientEmail ? 'border-red-500' : 'border-gray-300'
                } bg-white py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm`}
              />
            </div>
            {validationErrors.clientEmail && (
              <p className="mt-1 text-sm text-red-500">{validationErrors.clientEmail}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Private Key {!isEdit && <span className="text-red-500">*</span>}
              {isEdit && (
                <span className="text-xs text-gray-500 ml-1">
                  (leave blank to keep existing key)
                </span>
              )}
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyIcon className="h-5 w-5 text-gray-400" />
              </div>
              <textarea
                value={privateKey}
                onChange={(e) => onChange('privateKey', e.target.value)}
                placeholder="-----BEGIN PRIVATE KEY-----\nXXXXXXXXXXXXXXXXXXXXXXXXXX\n-----END PRIVATE KEY-----"
                rows={4}
                className={`pl-10 block w-full rounded-md border ${
                  validationErrors.privateKey ? 'border-red-500' : 'border-gray-300'
                } bg-white py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm`}
              />
            </div>
            {validationErrors.privateKey && (
              <p className="mt-1 text-sm text-red-500">{validationErrors.privateKey}</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Endpoint{' '}
            <span className="text-xs text-gray-500 ml-1">
              (leave empty for Google Cloud Storage)
            </span>
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <LinkIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={endpoint}
              onChange={(e) => onChange('endpoint', e.target.value)}
              placeholder="https://storage.googleapis.com"
              className="pl-10 block w-full rounded-md border border-gray-300 bg-white py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GCSFields;
